import type { GetContentSalesforceObjectAssociationsRequest } from "@amzn/ads-blink-client";
import { SalesforceObjectType } from "@amzn/ads-blink-client";
import type { PitchedContent } from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    Content,
    ContentSalesforceObjectsAssociation,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import { ContentType } from "@amzn/ads-measurement-plm-service-type-script-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";

import { getClientId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPlmApiClient } from "../../api/plmApi/plmApi";
import { authStore } from "../../store/auth";
import type { AssociatedPitchedContent } from "../../store/editContent";
import { notificationStore, NotificationType } from "../../store/notification";
import { getAdvertiserId } from "../../utils/permission.utils";
import {
    getPLMMigrationPhase,
    PLMMigrationPhase,
} from "../../utils/plmMigrationApi.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

const logAndNotifyError = (contextMessage: string, error: unknown) => {
    console.error(contextMessage, error);
    notificationStore
        .getState()
        .upsertNotification(
            `${contextMessage}. Please try again, or contact support.`,
            NotificationType.ERROR,
        );
};

export function usePitchedContentsAction(): [
    AsyncState<AssociatedPitchedContent[]>,
    UseAsyncActions<AssociatedPitchedContent[]>,
] {
    const accountId = getAdvertiserId();

    const [contentsState, contentsAction] = useAsync<
        AssociatedPitchedContent[]
    >(async () => {
        const response = await fetchPitchedContentFromACC();
        const notInProgressContents: PitchedContent[] = response.contents ?? [];

        return handleSfActivityWorkflow(notInProgressContents, accountId);
    }, []);

    useAsyncErrorStatusNotification(contentsState);

    return [contentsState, contentsAction];
}

const handleSfActivityWorkflow = async (
    notInProgressContents: PitchedContent[],
    accountId?: string,
): Promise<AssociatedPitchedContent[]> => {
    const contentsNeedingAssociations: Content[] = notInProgressContents.map(
        mapPitchedContentToContent,
    );

    let associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();
    if (contentsNeedingAssociations.length) {
        const associationResponse = await fetchSfAssociationsFromPLM(
            contentsNeedingAssociations,
        );

        associationsMap = buildAssociationsMap(associationResponse, accountId);
    }

    const mappedContents = attachAssociations(
        notInProgressContents,
        associationsMap,
    );

    const withFilteredOpps = mappedContents.map((content) =>
        filterContentForAccount(content, accountId),
    );
    return excludeIfNoOppAndNoAssoc(withFilteredOpps);
};

const attachAssociations = (
    pitchedContents: PitchedContent[], // From ACC
    associationsMap: Map<string, ContentSalesforceObjectsAssociation[]>, // From PLM
): AssociatedPitchedContent[] => {
    return pitchedContents.map((pitchedContent) => {
        const contentId = pitchedContent.contentId;
        const salesforceAssociations = associationsMap.get(contentId!) ?? [];
        return {
            ...pitchedContent,
            associations: salesforceAssociations,
        };
    });
};

const filterContentForAccount = (
    content: AssociatedPitchedContent,
    accountId?: string,
): AssociatedPitchedContent => {
    if (accountId && content.accountOpportunities) {
        const filteredOpps = content.accountOpportunities.filter(
            (opportunity) => opportunity.accountId === accountId,
        );
        return { ...content, accountOpportunities: filteredOpps };
    }
    return content;
};

const excludeIfNoOppAndNoAssoc = (
    contents: AssociatedPitchedContent[],
): AssociatedPitchedContent[] => {
    const phase = getPLMMigrationPhase();

    return contents.filter((c) => {
        const isSalesforceAssociationsEmpty = isEmpty(c.associations);

        // In Phase 2 and Phase 3, we only rely on PLM data (associations)
        // Filter out if there are no associations, regardless of accountOpportunities
        if (
            phase === PLMMigrationPhase.PHASE_2 ||
            phase === PLMMigrationPhase.PHASE_3
        ) {
            return !isSalesforceAssociationsEmpty;
        }

        // In Legacy and Phase 1, keep content if it has either opportunities or associations
        const isAccountOpportunitiesEmpty = isEmpty(c.accountOpportunities);
        return !isAccountOpportunitiesEmpty || !isSalesforceAssociationsEmpty;
    });
};

const buildAssociationsMap = (
    response: {
        contentSalesforceObjectAssociations?: ContentSalesforceObjectsAssociation[];
    },
    accountId?: string,
): Map<string, ContentSalesforceObjectsAssociation[]> => {
    const associationsMap = new Map<
        string,
        ContentSalesforceObjectsAssociation[]
    >();

    response.contentSalesforceObjectAssociations
        ?.filter(
            (
                item,
            ): item is ContentSalesforceObjectsAssociation & {
                contentId: string;
            } =>
                !!item.content?.contentId && accountId !== undefined
                    ? item.salesforceAssociation?.salesforceAccountId ===
                      accountId
                    : false,
        )
        .forEach((item) => {
            const contentId = item.content?.contentId;
            const existing = associationsMap.get(contentId!) ?? [];
            existing.push(item);
            associationsMap.set(contentId!, existing);
        });
    return associationsMap;
};

const mapPitchedContentToContent = (
    pitchedContent: PitchedContent,
): Content => {
    const { contentId, contentType } = pitchedContent;
    const validContentTypes = Object.values(ContentType);

    if (contentType === "PITCH") {
        return {
            contentId,
            contentType: ContentType.PITCHED_CONTENT,
        };
    }
    if (validContentTypes.includes(contentType as ContentType)) {
        return {
            contentId,
            contentType: contentType as ContentType,
        };
    }
    return {
        contentId,
        contentType: undefined,
    };
};

const fetchSfAssociationsFromPLM = async (contents: Content[]) => {
    const phase = getPLMMigrationPhase();
    switch (phase) {
        case PLMMigrationPhase.PHASE_1:
            return await fetchOnlyActivityAssociationsFromPLMBlinkcall(
                contents,
            );
        case PLMMigrationPhase.PHASE_2:
        case PLMMigrationPhase.PHASE_3:
            return await fetchSfAssociationsFromPLMBlinkcall(contents);
        case PLMMigrationPhase.LEGACY:
        default:
            return await fetchOnlyActivityAssociationsFromPLMDirectcall(
                contents,
            );
    }
};

/**  
    API calls
*/

const fetchPitchedContentFromACC = async () => {
    try {
        return await (await getDiscoveryApiClient()).getPitchedContent();
    } catch (error) {
        logAndNotifyError("Error fetching pitched content from ACC", error);
        return { contents: [] };
    }
};

// Legacy phase
const fetchOnlyActivityAssociationsFromPLMDirectcall = async (
    contents: Content[],
) => {
    try {
        return await (
            await getPlmApiClient()
        ).getContentSfAssociation(contents);
    } catch (error) {
        logAndNotifyError(
            "Error fetching Salesforce associations from PLM (direct call)",
            error,
        );
        return {
            contentSalesforceObjectAssociations: [],
        };
    }
};

// Phase1: Dual write but still fetching opportunity from ACC and Activity from PLM (via blink)
const fetchOnlyActivityAssociationsFromPLMBlinkcall = async (
    contents: Content[],
) => {
    try {
        const request: GetContentSalesforceObjectAssociationsRequest = {
            userAlias: authStore.getState().username,
            clientId: getClientId(),
            contents,
        };
        const response = await (
            await getBlinkApiClientV2()
        ).getContentSalesforceObjectAssociations(request);

        // Filter to only include associations where salesforceObjectType is ACTIVITY
        const filteredAssociations =
            response.contentSalesforceObjectAssociations?.filter(
                (association) =>
                    association.salesforceAssociation?.salesforceObjectType ===
                    SalesforceObjectType.ACTIVITY,
            ) ?? [];

        return {
            contentSalesforceObjectAssociations: filteredAssociations,
        };
    } catch (error) {
        logAndNotifyError(
            "Error fetching Activity associations from PLM via Blink",
            error,
        );
        return {
            contentSalesforceObjectAssociations: [],
        };
    }
};

// Phase 2 and Phase 3: Once we have migrated Opportunity data to PLM and only fetch associations from PLM (vis Blink)
const fetchSfAssociationsFromPLMBlinkcall = async (contents: Content[]) => {
    try {
        const request: GetContentSalesforceObjectAssociationsRequest = {
            userAlias: authStore.getState().username,
            clientId: getClientId(),
            contents,
        };
        const response = await (
            await getBlinkApiClientV2()
        ).getContentSalesforceObjectAssociations(request);

        return {
            contentSalesforceObjectAssociations:
                response.contentSalesforceObjectAssociations ?? [],
        };
    } catch (error) {
        logAndNotifyError(
            "Error fetching Salesforce associations from PLM via Blink",
            error,
        );
        return {
            contentSalesforceObjectAssociations: [],
        };
    }
};
